// Конфиг suggestion для TipTap @mention: выпадашка участников по «@».
// Imperative-рендер (TipTap так требует) — лёгкий попап в document.body,
// позиционируется по курсору. Items берём через getter, чтобы видеть актуальный
// список участников (редактор создаётся один раз, а участники подгружаются позже).

import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion'

export interface MentionItem {
  id: string
  label: string
}

export function buildMentionSuggestion(getItems: () => MentionItem[]): Omit<SuggestionOptions<MentionItem>, 'editor'> {
  return {
    char: '@',
    items: ({ query }): MentionItem[] => {
      const q = query.toLowerCase()
      return getItems()
        .filter((item) => item.label.toLowerCase().includes(q))
        .slice(0, 8)
    },
    render: () => {
      let el: HTMLDivElement | null = null
      let items: MentionItem[] = []
      let selectedIndex = 0
      let command: ((item: MentionItem) => void) | null = null

      function paint(): void {
        if (!el) return
        el.innerHTML = ''
        if (items.length === 0) {
          const empty = document.createElement('div')
          empty.className = 'mention-empty'
          empty.textContent = 'Нет участников'
          el.appendChild(empty)
          return
        }
        items.forEach((item, index) => {
          const btn = document.createElement('button')
          btn.type = 'button'
          btn.className = 'mention-item' + (index === selectedIndex ? ' is-selected' : '')
          btn.textContent = item.label
          // mousedown (не click), чтобы не терять фокус/выделение редактора до вставки.
          btn.addEventListener('mousedown', (event) => {
            event.preventDefault()
            command?.(item)
          })
          el!.appendChild(btn)
        })
      }

      function position(rect: DOMRect | null | undefined): void {
        if (!el || !rect) return
        el.style.left = `${rect.left}px`
        el.style.top = `${rect.bottom + 4}px`
      }

      return {
        onStart: (props: SuggestionProps<MentionItem>): void => {
          items = props.items
          selectedIndex = 0
          command = (item) => props.command({ id: item.id, label: item.label })
          el = document.createElement('div')
          el.className = 'mention-suggest'
          document.body.appendChild(el)
          paint()
          position(props.clientRect?.())
        },
        onUpdate: (props: SuggestionProps<MentionItem>): void => {
          items = props.items
          selectedIndex = 0
          command = (item) => props.command({ id: item.id, label: item.label })
          paint()
          position(props.clientRect?.())
        },
        onKeyDown: (props: SuggestionKeyDownProps): boolean => {
          if (props.event.key === 'Escape') {
            return true
          }
          if (items.length === 0) {
            return false
          }
          if (props.event.key === 'ArrowDown') {
            selectedIndex = (selectedIndex + 1) % items.length
            paint()
            return true
          }
          if (props.event.key === 'ArrowUp') {
            selectedIndex = (selectedIndex - 1 + items.length) % items.length
            paint()
            return true
          }
          if (props.event.key === 'Enter') {
            const item = items[selectedIndex]
            if (item) command?.(item)
            return true
          }
          return false
        },
        onExit: (): void => {
          el?.remove()
          el = null
          items = []
          command = null
        }
      }
    }
  }
}
